import { apiClient } from './client';

// スケジュール（定時 / 繰り返し実行）API。バックエンド = NestJS CRUD（タスク #90）。
// 発火基盤（Backend 1分interval常駐ポーラ）= タスク #91 / ADR-0004。親タスク #79（UI）。
//
// 日時は API 境界で変換する：
//   - サーバは DateTime を ISO(UTC) で返す → 画面表示/編集用にローカル "YYYY-MM-DDTHH:mm" へ。
//   - 送信時は datetime-local の "YYYY-MM-DDTHH:mm"（ローカル）→ ISO へ。

// ── 型 ────────────────────────────────────────────────────────────

export type ScheduleType = 'one_time' | 'recurring';

// 繰り返し指定。UI で組み立てやすい構造体。サーバ側で cron 式へ正規化して 1分ポーラが評価する。
export type Recurrence =
  | { kind: 'hourly'; minute: number } // 毎時 mm 分
  | { kind: 'daily'; hour: number; minute: number } // 毎日 HH:mm
  | { kind: 'weekly'; weekday: number; hour: number; minute: number } // 毎週 [曜] HH:mm
  | { kind: 'cron'; expr: string }; // 上級者向け生 cron 式

export interface ScheduledJob {
  id: number;
  name: string;
  scheduleType: ScheduleType;
  // one_time のとき: ローカル日時文字列 "YYYY-MM-DDTHH:mm"（API 境界で ISO から変換済み）
  runAt: string | null;
  recurrence: Recurrence | null;
  projectId: number | null;
  prompt: string;
  enabled: boolean;
  // 表示用（サーバが算出）。ローカル "YYYY-MM-DDTHH:mm" に変換済み。
  nextRunAt: string | null;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ScheduleInput {
  name: string;
  scheduleType: ScheduleType;
  runAt?: string | null; // ローカル "YYYY-MM-DDTHH:mm"
  recurrence?: Recurrence | null;
  projectId?: number | null;
  prompt: string;
  enabled?: boolean;
}

// 一覧画面用：サーバ側 検索/ソート/ページング クエリ（backend SCHEDULE_SORT_FIELDS と一致）。
export type ScheduleSortField = 'id' | 'name' | 'nextRunAt' | 'createdAt';
export interface ListSchedulesParams {
  q?: string; // name/prompt のあいまい検索
  projectId?: number; // 対象プロジェクトでの絞り込み（ドロップダウン）
  enabled?: boolean; // 有効/無効での絞り込み（ドロップダウン）
  scheduleType?: ScheduleType; // 種別での絞り込み（ドロップダウン）
  sortBy?: ScheduleSortField;
  sortDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}
export interface PaginatedSchedules {
  items: ScheduledJob[];
  total: number;
  page: number;
  pageSize: number;
}

// ── 表示ヘルパ ────────────────────────────────────────────────────

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];
const pad2 = (n: number) => String(n).padStart(2, '0');

// 人が読める日本語サマリ。
export function describeSchedule(job: ScheduledJob): string {
  if (job.scheduleType === 'one_time') {
    return job.runAt ? `定時 1回 ・ ${job.runAt.replace('T', ' ')}` : '定時 1回（未設定）';
  }
  const r = job.recurrence;
  if (!r) return '繰り返し（未設定）';
  switch (r.kind) {
    case 'hourly':
      return `毎時 ${pad2(r.minute)}分`;
    case 'daily':
      return `毎日 ${pad2(r.hour)}:${pad2(r.minute)}`;
    case 'weekly':
      return `毎週 ${WEEKDAYS[r.weekday]}曜 ${pad2(r.hour)}:${pad2(r.minute)}`;
    case 'cron':
      return `cron: ${r.expr}`;
  }
}

// ── 日時変換（API 境界）────────────────────────────────────────────

// ISO(UTC) → ローカル "YYYY-MM-DDTHH:mm"。null/空は null。
function isoToLocalInput(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
// ローカル "YYYY-MM-DDTHH:mm" → ISO(UTC)。
function localInputToIso(s: string | null | undefined): string | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// サーバ生レスポンス（日時が ISO）→ フロント ScheduledJob（日時がローカル文字列）。
interface RawJob extends Omit<ScheduledJob, 'runAt' | 'nextRunAt' | 'lastRunAt'> {
  runAt: string | null;
  nextRunAt: string | null;
  lastRunAt: string | null;
}
function mapJob(raw: RawJob): ScheduledJob {
  return {
    ...raw,
    runAt: isoToLocalInput(raw.runAt),
    nextRunAt: isoToLocalInput(raw.nextRunAt),
    lastRunAt: isoToLocalInput(raw.lastRunAt),
  };
}

// 入力（ローカル日時）→ サーバ送信形（runAt を ISO へ）。
function toPayload(input: Partial<ScheduleInput>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...input };
  if ('runAt' in input) out.runAt = localInputToIso(input.runAt);
  return out;
}

// ── 公開 API ──────────────────────────────────────────────────────

const BASE = '/api/admin/schedules';

export const scheduleApi = {
  list: (): Promise<ScheduledJob[]> =>
    apiClient.get<RawJob[]>(BASE).then((r) => r.data.map(mapJob)),
  // 一覧画面用：サーバ側 検索/ソート/ページング（list は他用途のため温存）。
  search: (params: ListSchedulesParams = {}): Promise<PaginatedSchedules> => {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.projectId !== undefined) qs.set('projectId', String(params.projectId));
    if (params.enabled !== undefined) qs.set('enabled', String(params.enabled));
    if (params.scheduleType) qs.set('scheduleType', params.scheduleType);
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.sortDir) qs.set('sortDir', params.sortDir);
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiClient
      .get<{ items: RawJob[]; total: number; page: number; pageSize: number }>(
        `${BASE}/search${suffix}`,
      )
      .then((r) => ({ ...r.data, items: r.data.items.map(mapJob) }));
  },
  create: (input: ScheduleInput): Promise<ScheduledJob> =>
    apiClient.post<RawJob>(BASE, toPayload(input)).then((r) => mapJob(r.data)),
  update: (id: number, input: Partial<ScheduleInput>): Promise<ScheduledJob> =>
    apiClient.patch<RawJob>(`${BASE}/${id}`, toPayload(input)).then((r) => mapJob(r.data)),
  setEnabled: (id: number, enabled: boolean): Promise<ScheduledJob> =>
    apiClient.post<RawJob>(`${BASE}/${id}/enabled`, { enabled }).then((r) => mapJob(r.data)),
  remove: (id: number): Promise<{ ok: true }> =>
    apiClient.delete<{ ok: true }>(`${BASE}/${id}`).then((r) => r.data),
};
